import { cn } from '@/lib/utils';
import DOMPurify from 'dompurify';

interface SectionTitleProps {
  id?: string;
  title: string;
  subtitle?: string;
  centered?: boolean;
  className?: string;
  isHero?: boolean;
}

/**
 * `dompurify` only ships an instance when `window` exists; on Node (during
 * RR v7 prerender) the default export is a factory and `sanitize` is undefined.
 * Returning the raw HTML during prerender lets the page render; the hydrated
 * client immediately replaces the markup with a sanitized version. Subtitles
 * come from build-time copy / static content, not user input — so the
 * prerendered HTML can't carry attacker-controlled markup.
 */
function safeSanitize(html: string): string {
  if (typeof window === 'undefined') return html;
  const sanitize = (DOMPurify as unknown as { sanitize?: (input: string) => string }).sanitize;
  return typeof sanitize === 'function' ? sanitize(html) : html;
}

export function SectionTitle({
  id,
  title,
  subtitle,
  centered = true,
  className,
  isHero = false,
}: SectionTitleProps) {
  const HeadingTag = isHero ? 'h1' : 'h2';

  return (
    <div className={cn('mb-12', centered && 'text-center', className)}>
      <HeadingTag
        id={id}
        className="mb-4 font-display text-3xl font-bold text-foreground md:text-4xl"
      >
        {title}
      </HeadingTag>
      {subtitle && (
        <div
          className="prose prose-lg mx-auto max-w-2xl text-lg text-muted-foreground md:text-xl"
          dangerouslySetInnerHTML={{ __html: safeSanitize(subtitle) }}
        />
      )}
    </div>
  );
}

export default SectionTitle;
